import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings] = useState(false);
  const [authError] = useState(null);
  const [appPublicSettings] = useState(null);

  const fetchProfile = async (authUser) => {
    if (!authUser) return authUser;
    try {
      // 1. Chercher par ID (cas normal)
      const { data: profile } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .single();

      if (profile) {
        console.log('Profile fetched by id:', profile.email);
        return { ...authUser, ...profile };
      }

      // 2. Fallback: chercher par email (user Base44 migré avec ancien UUID)
      const { data: legacyProfile } = await supabase
        .from('users')
        .select('*')
        .eq('email', authUser.email)
        .maybeSingle();

      if (legacyProfile) {
        console.log('Legacy profile found by email, migrating id:', legacyProfile.email);
        // Mettre à jour l'ancien UUID vers le nouveau UUID Supabase Auth
        await supabase
          .from('users')
          .update({ id: authUser.id })
          .eq('id', legacyProfile.id);
        return { ...authUser, ...legacyProfile, id: authUser.id };
      }

      // 3. Aucun profil — nouveau user, créer automatiquement
      console.log('No profile found, creating one for:', authUser.email);
      const { data: newProfile, error: upsertError } = await supabase
        .from('users')
        .upsert({
          id: authUser.id,
          email: authUser.email,
          full_name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || null,
          username: null,
          role: 'user',
          access_status: 'PENDING',
          onboarding_completed: false
        })
        .select()
        .single();

      if (upsertError) {
        console.error('Failed to create profile:', upsertError.message, upsertError.code);
      }

      return newProfile ? { ...authUser, ...newProfile } : authUser;
    } catch (err) {
      console.error('fetchProfile error:', err);
      return authUser;
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log('Auth state changed:', _event, session?.user?.email);
      if (session?.user) {
        setUser(session.user);
        setIsAuthenticated(true);
        setIsLoadingAuth(false);
        // Enrichir le profil en arrière-plan
        fetchProfile(session.user).then(enrichedUser => {
          setUser(enrichedUser);
        });
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setIsLoadingAuth(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const logout = async () => {
    await supabase.auth.signOut();
  };

  const navigateToLogin = () => {
    window.location.href = '/login';
  };

  const checkAppState = () => {};

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoadingAuth,
      isLoadingPublicSettings,
      authError,
      appPublicSettings,
      logout,
      navigateToLogin,
      checkAppState
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};
